import { motion } from 'framer-motion'
import {
  Sparkles,
  Globe,
  AlertCircle,
  ExternalLink,
  Copy,
  Check,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/utils/cn'
import { Button, Badge, Card, Markdown } from '@/components/common'
import type { AAISuggestionResult, Source } from '@/types/conversation'

export interface AAISuggestionProps {
  suggestion: AAISuggestionResult
  onAccept: (text: string) => void
  onAppend: (text: string) => void
  onDismiss: () => void
  className?: string
}

export const AAISuggestion = ({
  suggestion,
  onAccept,
  onAppend,
  onDismiss,
  className,
}: AAISuggestionProps) => {
  const [copied, setCopied] = useState(false)
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(suggestion.suggestion)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const confidenceColor =
    suggestion.confidence >= 0.8
      ? 'text-green-600 dark:text-green-400'
      : suggestion.confidence >= 0.6
      ? 'text-yellow-600 dark:text-yellow-400'
      : 'text-red-600 dark:text-red-400'

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={className}
    >
      <Card className="overflow-hidden border-mauve/30 dark:border-mauve/20">
        {/* Header */}
        <div className="p-4 bg-mauve/10 dark:bg-mauve/5 border-b border-mauve/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-mauve" />
              <span className="font-medium text-slate-900 dark:text-white">
                AAI Suggestion
              </span>
              {suggestion.webSearchUsed && (
                <Badge size="sm" className="bg-icy-blue/20 text-icy-blue dark:bg-muted-teal/20 dark:text-muted-teal">
                  <Globe className="w-3 h-3 mr-1" />
                  Web Search
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className={cn('text-sm font-medium', confidenceColor)}>
                {Math.round(suggestion.confidence * 100)}% confident
              </span>
            </div>
          </div>
        </div>

        {/* Main Suggestion */}
        <div className="p-4">
          <Markdown className="text-slate-700 dark:text-slate-300">
            {suggestion.suggestion}
          </Markdown>
        </div>

        {/* Key Facts */}
        {suggestion.keyFacts && suggestion.keyFacts.length > 0 && (
          <div className="px-4 pb-4">
            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Key Facts:
            </h4>
            <ul className="space-y-1">
              {suggestion.keyFacts.map((fact, index) => (
                <li
                  key={index}
                  className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400"
                >
                  <span className="text-mauve">•</span>
                  <Markdown inline>{fact}</Markdown>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Caveats */}
        {suggestion.caveats && suggestion.caveats.length > 0 && (
          <div className="px-4 pb-4">
            <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800/30">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                    Caveats:
                  </h4>
                  <ul className="mt-1 space-y-1">
                    {suggestion.caveats.map((caveat, index) => (
                      <li
                        key={index}
                        className="text-sm text-yellow-700 dark:text-yellow-400"
                      >
                        <Markdown inline>{caveat}</Markdown>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sources */}
        {suggestion.sources && suggestion.sources.length > 0 && (
          <div className="px-4 pb-4">
            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Sources:
            </h4>
            <div className="space-y-2">
              {suggestion.sources.map((source, index) => (
                <SourceItem key={index} source={source} />
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            {/* Feedback */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Was this helpful?</span>
              <button
                onClick={() => setFeedback('up')}
                className={cn(
                  'p-1.5 rounded-lg transition-colors',
                  feedback === 'up'
                    ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                    : 'hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500'
                )}
              >
                <ThumbsUp className="w-4 h-4" />
              </button>
              <button
                onClick={() => setFeedback('down')}
                className={cn(
                  'p-1.5 rounded-lg transition-colors',
                  feedback === 'down'
                    ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                    : 'hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500'
                )}
              >
                <ThumbsDown className="w-4 h-4" />
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleCopy}>
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </>
                )}
              </Button>
              <Button variant="ghost" size="sm" onClick={onDismiss}>
                Dismiss
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onAppend(suggestion.suggestion)}
              >
                Add to Answer
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => onAccept(suggestion.suggestion)}
              >
                Use as Answer
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  )
}

// Source Item Component
const SourceItem = ({ source }: { source: Source }) => (
  <a
    href={source.url}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors group"
  >
    <Globe className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate group-hover:text-icy-blue dark:group-hover:text-muted-teal">
        {source.title}
      </p>
      {source.snippet && (
        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mt-0.5">
          {source.snippet}
        </p>
      )}
    </div>
    <ExternalLink className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
  </a>
)

export default AAISuggestion
